import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';

const branchInfo = `
  id
  brand
  brandDisplayName
  company
  locationId
  locationName
  name
  suburb
  postcode
  state
  enabled
  dateCreated
  lastUpdated
  createdBy
  dealerLicenceNo
  dealerLicenceExpiry
  dealerPrincipalId
  turnOffAdvertsWhenStockIsSold
  externalReferenceId
  address
  abn
  phone
  carsalesDealerId,
  facebookPageId,
  facebookCatalogId,
  facebookLastPostDate,
  facebookPostSchedule,
  facebookAdTextBefore,
  facebookAdTextAfter,
  fbPageAccessToken,
  advertisingPlatforms {
      website,
      carsalesComAu,
      facebookMarketPlace,
      instagram,
      wholesaleClassifieds
  },
  adTextBefore,
  adTextAfter,
  erpBranchId,
  erpDatabase,
  vehiclePriorityFields,
  datafeedSource,
  numberOfVehicles
`;

const initialState = {
  isLoading: false,
  error: false,
  errors: {},
  branchList: [],
  successfullyMovedBranch: false,
  selectedBranch: {
    id: '',
    brand: '',
    brandDisplayName: '',
    company: '',
    locationId: '',
    locationName: '',
    name: '',
    suburb: '',
    postcode: '',
    state: '',
    enabled: false,
    dateCreated: '',
    lastUpdated: '',
    createdBy: '',
    dealerLicenceNo: '',
    dealerLicenceExpiry: '',
    dealerPrincipalId: '',
    turnOffAdvertsWhenStockIsSold: false,
    externalReferenceId: '',
    address: '',
    abn: '',
    phone: '',
    carsalesDealerId: '',
    facebookPageId: '',
    facebookCatalogId: '',
    facebookLastPostDate: '',
    facebookPostSchedule: '',
    facebookAdTextBefore: '',
    facebookAdTextAfter: '',
    fbPageAccessToken: '',
    advertisingPlatforms: {
      website: false,
      carsalesComAu: false,
      facebookMarketPlace: false,
      instagram: false,
      wholesaleClassifieds: false
    },
    erpBranchId: '',
    vehiclePriorityFields: '',
    erpDatabase: '',
    datafeedSource: ''
  }
};

const slice = createSlice({
  name: 'branch',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET BRANCH LIST
    getBranchListSuccess(state, action) {
      state.isLoading = false;
      state.branchList = action.payload;
      /* remove branchList error */
      delete state.errors.branchList;
      // const { branchList, ...otherErrors } = state.errors;
      // state.errors = otherErrors;
    },
    getBranchListError(state, action) {
      state.isLoading = false;
      state.errors = { ...state.errors, branchList: action.payload };
    },

    // GET BRANCH
    getBranchSuccess(state, action) {
      console.debug(action.payload);
      state.isLoading = false;
      state.selectedBranch = action.payload;
      state.successfullyMovedBranch = false;
      state.vehiclesMovedToNewBrand = [];
    },

    getBranchError(state, action) {
      console.debug(action.payload);
      state.isLoading = false;
      state.selectedBranch = null;
    },

    // CREATE BRANCH
    createBranchSuccess(state, action) {
      state.isLoading = false;
      state.selectedBranch = action.payload;
    },

    updateBranchSuccess(state, action) {
      state.isLoading = false;
      state.selectedBranch = action.payload;
    },

    clearBranchDataSuccess(state) {
      state.selectedBranch = initialState.selectedBranch;
    },

    clearBranchListSuccess(state) {
      state.branchList = initialState.branchList;
    },
    moveBranchToNewBrandSuccess(state, action) {
      state.isLoading = false;
      state.successfullyMovedBranch = true;
    },
    deleteBranchSuccess(state, action) {
      state.isLoading = false;
      const updatedBranchList = state.branchList.filter(
        (i) => i.id !== action.payload
      );
      state.branchList = Object.values(updatedBranchList);
    },
    enableBranchSuccess(state, action) {
      state.isLoading = false;
      const items = state.branchList.filter((i) => i.id !== action.payload);
      const selectedItem = state.branchList.filter(
        (i) => i.id === action.payload
      );
      const item = { ...selectedItem[0], enabled: true };
      const updatedBranchList = { ...items, item };
      state.branchList = Object.values(updatedBranchList);
    },
    disableBranchSuccess(state, action) {
      state.isLoading = false;
      const items = state.branchList.filter((i) => i.id !== action.payload);
      const selectedItem = state.branchList.filter(
        (i) => i.id === action.payload
      );
      const item = { ...selectedItem[0], enabled: false };
      const updatedBranchList = { ...items, item };
      state.branchList = Object.values(updatedBranchList);
    },
    updateBranchAdvertisingPlatformSuccess(state, action) {
      state.isLoading = false;
      state.branchList = state.branchList.map((item) =>
        item.id === action.payload.branchId
          ? {
              ...item,
              advertisingPlatforms: action.payload.advertisingPlatforms
            }
          : item
      );
    }
  }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function clearBranchData() {
  return async (dispatch) => {
    dispatch(slice.actions.clearBranchDataSuccess());
  };
}

export function clearBranchList() {
  return async (dispatch) => {
    dispatch(slice.actions.clearBranchListSuccess());
  };
}

// ----------------------------------------------------------------------
export function getBranchList() {
  // console.debug('getBranchList()');
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query {
        getBranchList {${branchInfo}}
      }`;
      const response = await axios.post('/graphql', { query: queryString });

      /* if returned errors, then throw an error */
      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0]);

      dispatch(
        slice.actions.getBranchListSuccess(response.data.data.getBranchList)
      );
    } catch (error) {
      console.debug('getBranchList got an error', error);
      const errorMessage = 'Failed to get branch options';
      // if (
      //   error.response &&
      //   error.response &&
      //   error.response.data &&
      //   error.response.data.errors
      // ) {
      //   errorMessage = error.response.data.errors[0].message;
      // }
      dispatch(slice.actions.getBranchListError(errorMessage));
    }
  };
}

export function getBranch(id, brand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    id = JSON.stringify(id);
    brand = JSON.stringify(brand);

    try {
      const queryString = `query {
        getBranch(id:${id}, brand:${brand}) {${branchInfo}}
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.getBranchSuccess(response.data.data.getBranch));
    } catch (error) {
      dispatch(slice.actions.getBranchError(error));
    }
  };
}

export async function getPageToken(userAccessToken, pageId) {
  userAccessToken = JSON.stringify(userAccessToken);
  pageId = JSON.stringify(pageId);
  let pageToken = '';
  try {
    const queryString = `query {
      getPageAccessToken(userAccessToken:${userAccessToken}, pageId:${pageId})
    }`;
    const response = await axios.post('/graphql', { query: queryString });
    pageToken = response.data.data.getPageAccessToken;
  } catch (error) {
    console.debug(error);
  }
  return pageToken;
}

export function createBranch(values) {
  const queryString = `mutation {
      createBranch(branch: {
        locationId:${JSON.stringify(values.locationId)}, 
        brand:${JSON.stringify(values.brand)}, 
        company:${JSON.stringify(values.company)},
        name:${JSON.stringify(values.name)}, 
        locationName:${JSON.stringify(values.locationName)}, 
        suburb:${JSON.stringify(values.suburb)}, 
        postcode:${JSON.stringify(values.postcode)}, 
        state:${JSON.stringify(values.state)}, 
        dealerLicenceNo:${JSON.stringify(values.dealerLicenceNo)},
        dealerLicenceExpiry:${JSON.stringify(values.dealerLicenceExpiry)},
        dealerPrincipalId:${JSON.stringify(
          values.dealerPrincipalId ? values.dealerPrincipalId : ''
        )}, 
        enabled:${JSON.stringify(values.enabled)},        
        turnOffAdvertsWhenStockIsSold:${JSON.stringify(
          values.turnOffAdvertsWhenStockIsSold
        )},
        externalReferenceId:${JSON.stringify(
          values.externalReferenceId ? values.externalReferenceId : ''
        )},
        address: ${JSON.stringify(values.address ? values.address : '')},
        abn: ${JSON.stringify(values.abn ? values.abn : '')},
        phone: ${JSON.stringify(values.phone ? values.phone : '')},
        carsalesDealerId: ${JSON.stringify(
          values.carsalesDealerId ? values.carsalesDealerId : ''
        )},
        facebookPageId: ${JSON.stringify(
          values.facebookPageId ? values.facebookPageId : ''
        )},
        facebookCatalogId: ${JSON.stringify(
          values.facebookCatalogId ? values.facebookCatalogId : ''
        )},
        facebookLastPostDate: ${JSON.stringify(values.facebookLastPostDate)},
        facebookPostSchedule: ${JSON.stringify(values.facebookPostSchedule)},
        facebookAdTextBefore: ${JSON.stringify(values.facebookAdTextBefore)},
        facebookAdTextAfter: ${JSON.stringify(values.facebookAdTextAfter)},
        fbPageAccessToken: ${JSON.stringify(values.fbPageAccessToken)},
        advertisingPlatforms: { 
          website: ${values.advertisingPlatforms.website},
          carsalesComAu: ${values.advertisingPlatforms.carsalesComAu},
          facebookMarketPlace: ${
            values.advertisingPlatforms.facebookMarketPlace
          },
          instagram: ${values.advertisingPlatforms.instagram},
          wholesaleClassifieds: ${
            values.advertisingPlatforms.wholesaleClassifieds
          }
          
        },
        adTextBefore: ${JSON.stringify(values.adTextBefore)},
        adTextAfter: ${JSON.stringify(values.adTextAfter)},
        erpBranchId: ${JSON.stringify(values.erpBranchId)},
        erpDatabase: ${JSON.stringify(values.erpDatabase)},
        vehiclePriorityFields: ${JSON.stringify(values.vehiclePriorityFields)},
        datafeedSource: ${JSON.stringify(values.datafeedSource)}
      }) {
        ${branchInfo}
      }
      }`;

  return async (dispatch) => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await excecuteQuery(queryString);

      dispatch(
        slice.actions.createBranchSuccess(response.data.data.createBranch)
      );
    } catch (error) {
      console.debug(error);
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export function updateBranch(values) {
  console.debug('updateBranch: values = ', values);
  // Add default values
  if (!values.advertisingPlatforms) {
    values.advertisingPlatforms = {
      website: true,
      instagram: false,
      facebookMarketPlace: false,
      carsalesComAu: false,
      wholesaleClassifieds: false
    };
  }

  const queryString = `mutation {
          updateBranch(branch: {
            id:${JSON.stringify(values.id)}, 
            locationId:${JSON.stringify(values.locationId)}, 
            brand:${JSON.stringify(values.brand)},  
            company:${JSON.stringify(values.company)},
            name:${JSON.stringify(values.name)}, 
            locationName:${JSON.stringify(values.locationName)}, 
            suburb:${JSON.stringify(values.suburb)}, 
            postcode:${JSON.stringify(values.postcode)}, 
            state:${JSON.stringify(values.state)}, 
            dealerLicenceNo:${JSON.stringify(values.dealerLicenceNo)},
            dealerLicenceExpiry:${JSON.stringify(values.dealerLicenceExpiry)},
            dealerPrincipalId:${JSON.stringify(values.dealerPrincipalId)}, 
            enabled:${JSON.stringify(values.enabled)}
            turnOffAdvertsWhenStockIsSold:${JSON.stringify(
              values.turnOffAdvertsWhenStockIsSold
            )},             
            externalReferenceId:${JSON.stringify(values.externalReferenceId)},
            address: ${JSON.stringify(values.address)},
            abn: ${JSON.stringify(values.abn)},
            phone: ${JSON.stringify(values.phone)},
            carsalesDealerId: ${JSON.stringify(values.carsalesDealerId)},
            facebookPageId: ${JSON.stringify(values.facebookPageId)},
            facebookCatalogId: ${JSON.stringify(values.facebookCatalogId)},
            facebookLastPostDate: ${JSON.stringify(
              values.facebookLastPostDate
            )},
            facebookPostSchedule: ${JSON.stringify(
              values.facebookPostSchedule
            )},
            facebookAdTextBefore: ${JSON.stringify(
              values.facebookAdTextBefore
            )},
            facebookAdTextAfter: ${JSON.stringify(values.facebookAdTextAfter)},
            fbPageAccessToken: ${JSON.stringify(values.fbPageAccessToken)},
            advertisingPlatforms: { 
              website: ${values.advertisingPlatforms.website},
              carsalesComAu: ${values.advertisingPlatforms.carsalesComAu},
              facebookMarketPlace: ${
                values.advertisingPlatforms.facebookMarketPlace
              },
              instagram: ${values.advertisingPlatforms.instagram},
              wholesaleClassifieds: ${
                values.advertisingPlatforms.wholesaleClassifieds
              }
            },
            adTextBefore: ${JSON.stringify(values.adTextBefore)},
            adTextAfter: ${JSON.stringify(values.adTextAfter)},
            erpBranchId: ${JSON.stringify(values.erpBranchId)},
            datafeedSource: ${JSON.stringify(values.datafeedSource)},
            erpDatabase: ${JSON.stringify(values.erpDatabase)},
            vehiclePriorityFields: ${JSON.stringify(
              values.vehiclePriorityFields
            )},
            dateCreated:${JSON.stringify(values.dateCreated)}, 
          }) {
              ${branchInfo}
            }
        }`;

  return async (dispatch) => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await excecuteQuery(queryString);

      dispatch(
        slice.actions.updateBranchSuccess(response.data.data.updateBranch)
      );
    } catch (error) {
      console.debug(error);
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export function moveBranchToNewBrand(branchId, currentBrand, newBrand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { 
        moveBranchVehiclesToNewBrand(id: "${branchId}",  currentBrand: "${currentBrand}", newBrand: "${newBrand}")
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.moveBranchToNewBrandSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function enableBranch(branchId, brand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { 
        enableBranch(id: "${branchId}",  brand: "${brand}")
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.enableBranchSuccess(branchId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export function disableBranch(branchId, brand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { 
        disableBranch(id: "${branchId}",  brand: "${brand}")
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.disableBranchSuccess(branchId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export function deleteBranch(branchId, brand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { 
        deleteBranch(id: "${branchId}",  brand: "${brand}")
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.deleteBranchSuccess(branchId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export function updateBranchAdvertisingPlatforms(
  branchId,
  brand,
  advertisingPlatforms,
  enableLoaders = true
) {
  return async (dispatch) => {
    if (enableLoaders) {
      dispatch(slice.actions.startLoading());
    }

    const updatedAdvertisingPlatforms = JSON.stringify(
      advertisingPlatforms
    ).replace(/"/g, '');

    try {
      const queryString = `mutation { 
        updateBranchAdvertisingPlatforms(id: "${branchId}",  brand: "${brand}", advertisingPlatforms: ${updatedAdvertisingPlatforms})
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(
        slice.actions.updateBranchAdvertisingPlatformSuccess({
          branchId,
          advertisingPlatforms
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw new Error(error);
    }
  };
}

export async function excecuteQuery(queryString) {
  const response = await axios.post('/graphql', { query: queryString });

  if (response.data.errors && response.data.errors.length > 0) {
    throw new Error(response.data.errors[0].message);
  }

  return response;
}
